<script setup lang="ts">
  defineOptions({ name: 'EventCard' })

  import { computed, type PropType } from 'vue'
  import { useI18n } from 'vue-i18n'

  import { Image01Icon } from '@gohighlevel/ghl-icons/24/outline'

  import { createElementId } from '@common/core-layer/utils'
  import OptimisedImage from '@common/media-layer/components/OptimisedImage.vue'
  import { useCPNavigation } from '@common/navigation-layer/composables/useNavigationContext'

  import CPText from '@ui/components/CPText.vue'

  import EventRegisteredBadge from '../atoms/EventRegisteredBadge.vue'
  import { toEventCoverObjectPosition } from '../../types/events-hub'
  import type { EventsHubCard } from '../../types/events-hub'

  const props = defineProps({
    event: {
      type: Object as PropType<EventsHubCard>,
      required: true,
    },
    idPrefix: {
      type: String,
      default: 'event',
    },
    priority: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits<{
    openEvent: [eventId: string]
  }>()

  const { t } = useI18n()
  const { getNavigationTarget } = useCPNavigation()
  const COVER_TONES = ['#EFF4FF', '#F4F3FF', '#ECFDF3', '#FFFAEB', '#FDF2FA'] as const

  const navigationTarget = computed(() => getNavigationTarget({ name: 'clientportal-events-detail', params: { eventId: props.event.id } }))
  const linkTo = computed(() => navigationTarget.value.url)
  const linkExternal = computed(() => navigationTarget.value.external)
  const showCoverImage = computed(() => Boolean(props.event.imageUrl?.trim()))
  const datePart = computed(() => props.event.dateChip?.trim() || '')
  const timePart = computed(() => props.event.timeLabel?.trim() || '')
  const dateTimeLine = computed(() => {
    if (datePart.value && timePart.value) {
      return `${datePart.value} ${t('clientportal.events.metaDot')} ${timePart.value}`
    }
    return timePart.value || datePart.value || props.event.dateLabel
  })

  const coverTone = computed(() => {
    let hash = 0
    for (const char of props.event.id) hash = (hash + char.charCodeAt(0)) % COVER_TONES.length
    return COVER_TONES[hash] ?? COVER_TONES[0]
  })

  const coverPosition = computed(() => toEventCoverObjectPosition(props.event.imageFocalPoint))
  const coverStyle = computed(() => {
    if (!showCoverImage.value) return { backgroundColor: coverTone.value }
    return coverPosition.value ? { '--event-cover-object-position': coverPosition.value } : undefined
  })

  const handleClick = () => {
    emit('openEvent', props.event.id)
  }
</script>

<template>
  <NuxtLink
    :id="createElementId(idPrefix, event.id, 'link')"
    :to="linkTo"
    :external="linkExternal"
    :aria-label="event.title"
    :title="event.title"
    class="event-card-link group flex w-full max-w-[25rem] no-underline focus:outline-none"
    @click="handleClick"
  >
    <article :id="createElementId(idPrefix, event.id, 'card')" class="event-card flex w-full flex-1 flex-col bg-background">
      <div class="event-card-image-wrapper relative w-full">
        <div
          class="event-card-cover relative flex aspect-[2/1] w-full items-center justify-center overflow-hidden rounded-t-lg"
          :style="coverStyle"
        >
          <OptimisedImage
            v-if="showCoverImage"
            :id="createElementId(idPrefix, event.id, 'cover')"
            :src="event.imageUrl"
            :alt="event.title"
            source="communities"
            :use-raw-url="true"
            gradient-mode="always"
            class="h-full w-full"
            :img-class="['object-cover transition-all duration-300', 'group-hover:scale-105'].join(' ')"
            fallback-aspect="aspect-[2/1]"
            :loading="priority ? 'eager' : 'lazy'"
            :fetchpriority="priority ? 'high' : 'auto'"
          />
          <div
            v-else
            :id="createElementId(idPrefix, event.id, 'cover-placeholder')"
            class="flex items-center justify-center"
            aria-hidden="true"
          >
            <Image01Icon class="event-card-cover-icon h-[1.625rem] w-[1.625rem]" />
          </div>
        </div>
        <div v-if="event.isRegistered" class="pointer-events-none absolute top-2 z-10" :class="event.dateChip ? 'left-2' : 'right-2'">
          <EventRegisteredBadge :id-prefix="idPrefix" :event-id="event.id" />
        </div>
        <div
          v-if="event.dateChip"
          :id="createElementId(idPrefix, event.id, 'date-chip')"
          class="pointer-events-none absolute right-2.5 top-2.5 z-10"
        >
          <CPText size="xs" weight="medium" html-tag="span" class="event-card-date-chip">
            {{ event.dateChip }}
          </CPText>
        </div>
      </div>

      <div class="relative z-10 flex flex-1 flex-col gap-1.5 px-3 py-3">
        <CPText
          :id="createElementId(idPrefix, event.id, 'title')"
          size="lg"
          weight="semibold"
          html-tag="h5"
          class="m-0 min-w-0 text-pretty leading-6 text-[var(--gray-900)]"
        >
          {{ event.title }}
        </CPText>
        <CPText
          v-if="dateTimeLine"
          :id="createElementId(idPrefix, event.id, 'time')"
          size="md"
          weight="semibold"
          class="m-0 truncate text-[var(--gray-900)]"
        >
          {{ dateTimeLine }}
        </CPText>
        <CPText
          v-if="event.locationLabel"
          :id="createElementId(idPrefix, event.id, 'place')"
          size="md"
          weight="normal"
          html-tag="span"
          class="min-w-0 truncate text-[var(--gray-400)]"
        >
          {{ event.locationLabel }}
        </CPText>
      </div>
    </article>
  </NuxtLink>
</template>

<style scoped>
  .event-card-link {
    border-radius: 8px;
  }

  .event-card {
    position: relative;
    z-index: 1;
    overflow: hidden;
    border-radius: 8px;
    border: 1px solid var(--cp-border-500);
    transition: all 0.3s ease;
  }

  .event-card-cover {
    color: var(--cp-font-400, var(--gray-400, #98a2b3));
  }

  .event-card-cover :deep(img) {
    object-fit: cover;
    object-position: var(--event-cover-object-position, center);
  }

  .event-card-date-chip {
    display: inline-block;
    background: rgba(16, 24, 40, 0.62);
    border-radius: 4px;
    padding: 3px 8px;
    color: #fff;
  }

  .event-card-link:hover .event-card,
  .event-card-link:focus-visible .event-card {
    transform: translateY(-4px);
    box-shadow: 0 12px 32px color-mix(in srgb, var(--cp-brand-font) 12%, transparent);
    z-index: 20;
    border-color: var(--cp-border-500);
  }
</style>
